import { useState, useEffect } from 'react';
import { X, User, Mail, Calendar, Ruler, Save, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, UpdateProfileDto } from '../services/user.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface UserSettingsProps {
  onClose: () => void;
}

export function UserSettings({ onClose }: UserSettingsProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateProfileDto>({
    username: '',
    displayName: '',
    bio: '',
    dateOfBirth: '',
    gender: '',
    height: undefined,
    shareData: true,
  });

  // Fetch user profile
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: () => userService.getProfile(),
    enabled: !!user,
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateProfileDto) => userService.updateProfile(data),
    onSuccess: () => {
      // Invalidate all profile-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast.success('Profile updated successfully!');
      onClose();
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  useEffect(() => {
    if (profile) {
      // Handle both direct profile object and nested structure
      const profileData = profile?.data || profile;
      const profileInfo = profileData?.profile || profileData;
      
      setFormData({
        displayName: profileInfo.displayName || profileData.displayName || '',
        bio: profileInfo.bio || profileData.bio || '',
        dateOfBirth: (profileInfo.dateOfBirth || profileData.dateOfBirth)
          ? new Date(profileInfo.dateOfBirth || profileData.dateOfBirth).toISOString().split('T')[0]
          : '',
        gender: profileInfo.gender || profileData.gender || '',
        height: profileInfo.height || profileData.height || undefined,
        shareData: profileInfo.shareData ?? profileData.shareData ?? true,
      });
    }
  }, [profile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleChange = (field: keyof UpdateProfileDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (error) {
    toast.error(handleApiError(error));
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">User Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <LoadingSkeleton variant="rectangular" height="3rem" />
            <LoadingSkeleton variant="rectangular" height="3rem" />
            <LoadingSkeleton variant="rectangular" height="3rem" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={formData.username || ''}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="your_username"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
              />
              <p className="text-xs text-gray-500 mt-1">Displayed in the community leaderboard</p>
            </div>

            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="displayName"
                  value={formData.displayName || ''}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  placeholder="Your Full Name"
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
                />
              </div>
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              <textarea
                id="bio"
                value={formData.bio || ''}
                onChange={(e) => handleChange('bio', e.target.value)}
                rows={3}
                placeholder="Tell us about yourself..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent resize-none text-gray-900"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  id="gender"
                  value={formData.gender || ''}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-2">
                Height (cm)
              </label>
              <div className="relative">
                <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  id="height"
                  value={formData.height || ''}
                  onChange={(e) => handleChange('height', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="e.g., 170"
                  min="50"
                  max="250"
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-dropsugar-green focus:border-transparent text-gray-900"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="shareData"
                checked={formData.shareData ?? true}
                onChange={(e) => handleChange('shareData', e.target.checked)}
                className="w-5 h-5 text-dropsugar-green border-gray-300 rounded focus:ring-dropsugar-green"
              />
              <label htmlFor="shareData" className="text-sm text-gray-700">
                Share my health data for analytics (anonymous)
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="flex-1 px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark disabled:bg-gray-400 text-white font-semibold rounded-xl transition-all disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
